/**
 * localStorage-backed shared location filters (Site / Department / Unit).
 *
 * Lifetime: browser `localStorage` — persists across tab close and browser restart.
 * Keyed by tenant + user so filters stay private per user and never leak across
 * users on the same browser or across tenants.
 * Not cleared on logout or session timeout; only the per-page Clear button clears
 * the current user+tenant bucket.
 *
 * REBUILD-393 / JW 392
 */

export interface LocationFilters {
  siteIds: number[]
  departmentIds: number[]
  unitIds: number[]
}

export type LocationFilterParamShape = 'dashboard' | 'instruments' | 'report'

export const LOCATION_FILTERS_STORAGE_PREFIX = 'ell.locationFilters.v1'

export const EMPTY_LOCATION_FILTERS: LocationFilters = {
  siteIds: [],
  departmentIds: [],
  unitIds: [],
}

export const locationFiltersStorageKey = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): string =>
  `${LOCATION_FILTERS_STORAGE_PREFIX}:tenant:${tenantId ?? 'unknown'}:user:${userId ?? 'unknown'}`

const normalizeIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => (typeof item === 'number' ? item : Number(item)))
    .filter((id) => Number.isInteger(id) && id > 0)
}

export const isLocationFiltersEmpty = (filters: LocationFilters): boolean =>
  filters.siteIds.length === 0 && filters.departmentIds.length === 0 && filters.unitIds.length === 0

/**
 * Returns true when every id in `subset` is also present in `superset`.
 * An empty subset is trivially a subset of any superset (vacuous truth).
 * Used by DashboardTabs to detect that the store is ahead of the URL (store was written
 * synchronously on filter pick; URL not yet updated because navigation is in flight).
 */
export const isLocationFiltersSubset = (
  subset: LocationFilters,
  superset: LocationFilters,
): boolean =>
  subset.siteIds.every((id) => superset.siteIds.includes(id)) &&
  subset.departmentIds.every((id) => superset.departmentIds.includes(id)) &&
  subset.unitIds.every((id) => superset.unitIds.includes(id))

export const readLocationFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): LocationFilters => {
  if (tenantId == null || typeof localStorage === 'undefined') {
    return { ...EMPTY_LOCATION_FILTERS }
  }

  try {
    const raw = localStorage.getItem(locationFiltersStorageKey(tenantId, userId))
    if (!raw) return { ...EMPTY_LOCATION_FILTERS }

    const parsed = JSON.parse(raw) as Partial<LocationFilters>
    return {
      siteIds: normalizeIds(parsed.siteIds),
      departmentIds: normalizeIds(parsed.departmentIds),
      unitIds: normalizeIds(parsed.unitIds),
    }
  } catch {
    return { ...EMPTY_LOCATION_FILTERS }
  }
}

/** Full replace of the store (Dashboard / NC / Instruments). */
export const writeLocationFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
  filters: LocationFilters,
): void => {
  if (tenantId == null || typeof localStorage === 'undefined') return

  const next: LocationFilters = {
    siteIds: normalizeIds(filters.siteIds),
    departmentIds: normalizeIds(filters.departmentIds),
    unitIds: normalizeIds(filters.unitIds),
  }

  if (isLocationFiltersEmpty(next)) {
    clearLocationFilters(tenantId, userId)
    return
  }

  try {
    localStorage.setItem(locationFiltersStorageKey(tenantId, userId), JSON.stringify(next))
  } catch {
    // QuotaExceededError / SecurityError — persistence is best-effort.
  }
}

/** Merge only provided keys — for surfaces that do not expose every location filter. */
export const mergeLocationFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
  partial: Partial<LocationFilters>,
): void => {
  const current = readLocationFilters(tenantId, userId)
  writeLocationFilters(tenantId, userId, {
    siteIds: partial.siteIds !== undefined ? partial.siteIds : current.siteIds,
    departmentIds:
      partial.departmentIds !== undefined ? partial.departmentIds : current.departmentIds,
    unitIds: partial.unitIds !== undefined ? partial.unitIds : current.unitIds,
  })
}

export const clearLocationFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): void => {
  if (tenantId == null || typeof localStorage === 'undefined') return
  localStorage.removeItem(locationFiltersStorageKey(tenantId, userId))
}

export const urlHasLocationParams = (search: string, shape: LocationFilterParamShape): boolean => {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)

  if (shape === 'dashboard') {
    return (
      params.has('site_id[]') ||
      params.has('department_id[]') ||
      params.has('unit_id[]') ||
      // Rails sometimes serializes without brackets in tests / edge clients
      params.has('site_id') ||
      params.has('department_id') ||
      params.has('unit_id')
    )
  }

  if (shape === 'instruments') {
    const siteIds = params.get('site_ids')
    const departmentIds = params.get('department_ids')
    const unitIds = params.get('unit_ids')
    return Boolean(siteIds || departmentIds || unitIds)
  }

  // Monthly / single-select report shape
  const siteId = params.get('site_id')
  const departmentId = params.get('department_id')
  return Boolean((siteId && siteId !== 'all') || (departmentId && departmentId !== 'all'))
}

interface PruneOption {
  value: number
  siteId?: number | null
  departmentId?: number | null
}

/**
 * Drop session ids the user can no longer access (option lists are already
 * location-scoped server-side). Cascades dept/unit against remaining parents.
 */
export const pruneLocationFiltersAgainstOptions = (
  filters: LocationFilters,
  siteOptions: Array<{ value: number }>,
  departmentOptions: PruneOption[] = [],
  unitOptions: PruneOption[] = [],
): LocationFilters => {
  const allowedSiteIds = new Set(siteOptions.map((option) => option.value))
  const siteIds = filters.siteIds.filter((id) => allowedSiteIds.has(id))

  const departmentIds = filters.departmentIds.filter((id) => {
    const department = departmentOptions.find((option) => option.value === id)
    if (!department) return false
    if (siteIds.length === 0) return true
    return department.siteId == null || siteIds.includes(department.siteId)
  })

  const unitIds = filters.unitIds.filter((id) => {
    const unit = unitOptions.find((option) => option.value === id)
    if (!unit) return false
    if (siteIds.length > 0 && unit.siteId != null && !siteIds.includes(unit.siteId)) return false
    if (
      departmentIds.length > 0 &&
      unit.departmentId != null &&
      !departmentIds.includes(unit.departmentId)
    ) {
      return false
    }
    return true
  })

  return { siteIds, departmentIds, unitIds }
}

/** Build `?site_id[]=…&department_id[]=…&unit_id[]=…` (or empty string). */
export const buildDashboardLocationQuery = (filters: LocationFilters): string => {
  const params = new URLSearchParams()
  filters.siteIds.forEach((id) => params.append('site_id[]', String(id)))
  filters.departmentIds.forEach((id) => params.append('department_id[]', String(id)))
  filters.unitIds.forEach((id) => params.append('unit_id[]', String(id)))
  const query = params.toString()
  return query ? `?${query}` : ''
}

/** Extract dashboard-shaped location filters from the current Inertia URL. */
export const extractDashboardLocationFiltersFromUrl = (url: string): LocationFilters => {
  const queryIndex = url.indexOf('?')
  const search = queryIndex >= 0 ? url.slice(queryIndex) : ''
  const params = new URLSearchParams(search)
  const readList = (bracketKey: string, bareKey: string): number[] => {
    const bracket = params
      .getAll(bracketKey)
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0)
    if (bracket.length > 0) return bracket
    return params
      .getAll(bareKey)
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0)
  }
  return {
    siteIds: readList('site_id[]', 'site_id'),
    departmentIds: readList('department_id[]', 'department_id'),
    unitIds: readList('unit_id[]', 'unit_id'),
  }
}

export const toInstrumentsCsvParams = (filters: LocationFilters): Record<string, string> => {
  const params: Record<string, string> = {}
  if (filters.siteIds.length > 0) params.site_ids = filters.siteIds.join(',')
  if (filters.departmentIds.length > 0) params.department_ids = filters.departmentIds.join(',')
  if (filters.unitIds.length > 0) params.unit_ids = filters.unitIds.join(',')
  return params
}

/** Parse Instruments CSV filter props (`"1,2"` / garbage) into positive integer ids. */
export const parseLocationIdCsv = (value: string | null | undefined): number[] => {
  if (!value) return []
  return normalizeIds(value.split(','))
}

/**
 * Monthly ReportFilters is single-select — first persisted site wins.
 * Department is the first id that belongs to that site when options are provided.
 * Returns sentinel-friendly strings (null means "all").
 */
export const toReportSingleSelect = (
  filters: LocationFilters,
  departmentOptions: Array<{ value: number; siteId?: number | null }> = [],
): { siteId: string | null; departmentId: string | null } => {
  const siteId = filters.siteIds[0] ?? null
  if (siteId == null) {
    return { siteId: null, departmentId: null }
  }

  const matchingDepartmentId =
    departmentOptions.length > 0
      ? (filters.departmentIds.find((departmentId) => {
          const department = departmentOptions.find((option) => option.value === departmentId)
          return department != null && (department.siteId == null || department.siteId === siteId)
        }) ?? null)
      : (filters.departmentIds[0] ?? null)

  return {
    siteId: String(siteId),
    departmentId: matchingDepartmentId != null ? String(matchingDepartmentId) : null,
  }
}
